import React from 'react';
import './css/common.css';
import './App.css';
import './components/AvatarEyes.css';
import { languages, strings } from "./languages/localizationStrings";
import LanguageSwitch from "./components/LanguageSwitch";
import { Cookies } from 'react-cookie';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSun, faMoon, faVolumeUp, faVolumeMute, faCog, faSignInAlt, faSignOutAlt, faGlobe } from "@fortawesome/free-solid-svg-icons";
import { getTheme, setTheme, applyTheme, getAudioEnabled, setAudioEnabled, getAvatar, setAvatar, getLastWorkout, setLastWorkout, getNickname, setNickname } from "./tools/localStorage";
import { auth, googleProvider, isFirebaseConfigured } from "./tools/firebase";
import { signInWithPopup, signOut, onAuthStateChanged } from "firebase/auth";
import { syncOnLogin, saveProfileToCloud } from "./tools/cloudSync";
import audioGuide from "./tools/AudioGuide";
import ProgressTracking from "./tools/progressTracking";
import iconImg from './icon.png';
import { exerciseDurations, exerciseCounts, getExerciseGroups } from './config/exerciseConfig';
import { trackThemeChange, trackAudioToggle, trackLanguageChange } from './tools/analytics';
import IrisMascot from './components/IrisMascot';
import { getIrisGreeting, getIrisMood } from './tools/irisPersonality';


const AVATAR_OPTIONS = [
    { key: 'dog', icon: '🐶' },
    { key: 'cat', icon: '🐱' },
    { key: 'owl', icon: '🦉' },
    { key: 'moose', icon: '🦌' },
    { key: 'classic', icon: '👁️' },
];

// Unique color + emoji for each exercise card
const CARD_STYLES = {
    beginner:            { emoji: '🌱', color: '#4CAF50' },
    intermediate:        { emoji: '💪', color: '#2196F3' },
    advanced:            { emoji: '🔥', color: '#FF9800' },
    intense:             { emoji: '⚡', color: '#F44336' },
    focusCatcher:        { emoji: '🎯', color: '#00E5FF' },
    progressiveEasy:     { emoji: '🎯', color: '#9C27B0' },
    progressiveMedium:   { emoji: '🌊', color: '#00BCD4' },
    progressiveTough:    { emoji: '🌸', color: '#E91E63' },
    geometricPatterns:   { emoji: '✨', color: '#FFC107' },
};

class App extends React.Component {
    constructor(props) {
        super(props);
        this.cookies = new Cookies();
        let language = strings.getLanguage();

        const currentTheme = applyTheme();
        const audioEnabled = getAudioEnabled();

        this.state = {
            language: language,
            theme: currentTheme,
            audioEnabled: audioEnabled,
            avatar: getAvatar(),
            avatarDropdownOpen: false,
            settingsOpen: false,
            user: null,
            nickname: getNickname(),
            syncing: false,
            comingSoon: false,
            secretBuffer: '',
            labUnlocked: false,
        }
        this.changeLanguage = this.changeLanguage.bind(this);
        this.toggleTheme = this.toggleTheme.bind(this);
        this.toggleAudio = this.toggleAudio.bind(this);
        this.handleExerciseClick = this.handleExerciseClick.bind(this);
        this.selectAvatar = this.selectAvatar.bind(this);
        this.handleSignIn = this.handleSignIn.bind(this);
        this.handleSignOut = this.handleSignOut.bind(this);
        this.handleNicknameChange = this.handleNicknameChange.bind(this);
    }

    componentDidMount() {
        // Set document lang attribute to match current language
        document.documentElement.lang = this.state.language;

        if (isFirebaseConfigured && auth) {
            this._unsubAuth = onAuthStateChanged(auth, (user) => {
                this.setState({ user });
            });
        }

        // Secret code listener: type "MAZE" anywhere to unlock experimental games
        this._onSecretKey = (e) => {
            // Ignore if user is typing in an input
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
            const key = e.key.toUpperCase();
            if (key.length !== 1) return;
            const buffer = (this.state.secretBuffer + key).slice(-4);
            const unlocked = buffer === 'MAZE';
            this.setState({ secretBuffer: buffer, labUnlocked: unlocked || this.state.labUnlocked });
        };
        document.addEventListener('keydown', this._onSecretKey);
    }

    componentWillUnmount() {
        if (this._unsubAuth) this._unsubAuth();
        if (this._comingSoonTimer) clearTimeout(this._comingSoonTimer);
        if (this._onSecretKey) document.removeEventListener('keydown', this._onSecretKey);
    }

    async handleSignIn() {
        if (!isFirebaseConfigured || !auth) {
            this.setState({ comingSoon: true });
            this._comingSoonTimer = setTimeout(() => this.setState({ comingSoon: false }), 4000);
            return;
        }
        try {
            this.setState({ syncing: true });
            await signInWithPopup(auth, googleProvider);
            const merged = await syncOnLogin();
            if (merged) {
                this.setState({
                    avatar: merged.avatar || this.state.avatar,
                    nickname: merged.nickname || this.state.nickname,
                });
            }
        } catch (err) {
            console.error('Sign-in failed:', err);
        } finally {
            this.setState({ syncing: false });
        }
    }

    async handleSignOut() {
        try {
            await signOut(auth);
            this.setState({ user: null });
        } catch (err) {
            console.error('Sign-out failed:', err);
        }
    }

    handleNicknameChange(e) {
        const name = e.target.value.slice(0, 20);
        setNickname(name);
        this.setState({ nickname: name });
        saveProfileToCloud(name, this.state.avatar).catch(() => {});
    }

    changeLanguage(lang) {
        strings.setLanguage(lang);
        this.cookies.set('lang', lang, { path: '/' });
        this.setState({ language: lang });
        document.documentElement.lang = lang;
        trackLanguageChange(lang);
    }

    toggleTheme() {
        let newTheme;
        if (this.state.theme === 'dark') {
            newTheme = 'light';
        } else if (this.state.theme === 'light') {
            newTheme = 'sepia';
        } else {
            newTheme = 'dark';
        }
        setTheme(newTheme);
        this.setState({ theme: newTheme });
        trackThemeChange(newTheme);
    }

    toggleAudio() {
        const newAudioState = !this.state.audioEnabled;
        setAudioEnabled(newAudioState);
        this.setState({ audioEnabled: newAudioState });
        trackAudioToggle(newAudioState);
        audioGuide.setEnabled(newAudioState);
        if (newAudioState) {
            audioGuide.unlockAudio();
        }
    }

    handleExerciseClick(e) {
        if (this.state.audioEnabled) {
            audioGuide.unlockAudio();
        }
    }

    selectAvatar(avatarKey) {
        setAvatar(avatarKey);
        this.setState({ avatar: avatarKey, avatarDropdownOpen: false });
        saveProfileToCloud(this.state.nickname, avatarKey).catch(() => {});
    }

    renderExerciseCard(level) {
        const style = CARD_STYLES[level.key] || { emoji: '👁️', color: '#4a90e2' };
        const duration = exerciseDurations[level.key];
        const count = exerciseCounts[level.key];

        return (
            <a
                href={`exercise${level.key.charAt(0).toUpperCase() + level.key.slice(1)}`}
                className="exercise-card"
                key={level.key}
                onClick={() => { setLastWorkout(level.key, level.name); this.handleExerciseClick(); }}
                style={{ '--card-accent': style.color }}
            >
                <div className="card-emoji">{style.emoji}</div>
                <div className="card-name">{level.name}</div>
                <div className="card-info">
                    <span>~{duration} min</span>
                    <span className="card-dot"></span>
                    <span>{strings.formatString(strings.exerciseCount, count)}</span>
                </div>
            </a>
        );
    }

    render() {
        const groups = getExerciseGroups();
        const allLevels = Object.values(groups).flatMap(g => g.levels);
        const lastWorkout = getLastWorkout();
        const stats = ProgressTracking.getStats();

        return (<div className="App">
            {/* Coming soon toast */}
            {this.state.comingSoon && (
                <div className="coming-soon-toast" onClick={() => this.setState({ comingSoon: false })}>
                    {strings.comingSoon}
                </div>
            )}

            {/* Simple top bar */}
            <div className="top-nav">
                <div className="nav-left">
                    <div className="nav-brand">
                        <img src={iconImg} alt="" className="nav-logo" />
                        <span>{strings.appName}</span>
                    </div>
                    {/* User profile - prominent position next to brand */}
                    {this.state.user ? (
                        <button
                            className="nav-user-btn"
                            onClick={() => this.setState(s => ({ settingsOpen: !s.settingsOpen }))}
                            title="Settings"
                        >
                            <span className="nav-user-avatar">
                                {AVATAR_OPTIONS.find(a => a.key === this.state.avatar)?.icon || '👁️'}
                            </span>
                            {this.state.nickname && <span className="nav-user-name">{this.state.nickname}</span>}
                        </button>
                    ) : (
                        <button
                            className="nav-sign-in-pill"
                            onClick={this.handleSignIn}
                            disabled={this.state.syncing}
                            title={strings.signIn}
                        >
                            <FontAwesomeIcon icon={faSignInAlt} />
                            <span className="sign-in-label">{strings.signIn}</span>
                        </button>
                    )}
                </div>
                <div className="nav-controls">
                    <button
                        className={`nav-icon-btn ${this.state.audioEnabled ? 'active' : ''}`}
                        onClick={this.toggleAudio}
                        title={this.state.audioEnabled ? strings.audioOn : strings.audioOff}
                        aria-label={this.state.audioEnabled ? strings.audioOn : strings.audioOff}
                    >
                        <FontAwesomeIcon icon={this.state.audioEnabled ? faVolumeUp : faVolumeMute} />
                    </button>
                    <button
                        className="nav-icon-btn"
                        onClick={this.toggleTheme}
                        title={
                            this.state.theme === 'dark' ? strings.lightMode :
                            this.state.theme === 'light' ? strings.sepiaMode :
                            strings.darkMode
                        }
                        aria-label={
                            this.state.theme === 'dark' ? strings.lightMode :
                            this.state.theme === 'light' ? strings.sepiaMode :
                            strings.darkMode
                        }
                    >
                        {this.state.theme === 'dark' ? <FontAwesomeIcon icon={faSun} /> :
                         this.state.theme === 'light' ? <span style={{fontSize: '18px'}}>☕</span> :
                         <FontAwesomeIcon icon={faMoon} />}
                    </button>

                    {/* Settings toggle for avatar & language */}
                    <button
                        className={`nav-icon-btn ${this.state.settingsOpen ? 'active' : ''}`}
                        onClick={() => this.setState(s => ({ settingsOpen: !s.settingsOpen }))}
                        title="Settings"
                        aria-label="Settings"
                    >
                        <FontAwesomeIcon icon={faCog} />
                    </button>
                </div>
            </div>

            {/* Collapsible settings panel */}
            {this.state.settingsOpen && (
                <>
                    <div className="settings-backdrop" onClick={() => this.setState({ settingsOpen: false })} />
                    <div className="settings-panel">
                        <div className="settings-section">
                            <div className="settings-label">{strings.avatarSelector}</div>
                            <div className="avatar-row">
                                {AVATAR_OPTIONS.map(opt => (
                                    <button
                                        key={opt.key}
                                        className={`avatar-pick ${this.state.avatar === opt.key ? 'active' : ''}`}
                                        onClick={() => this.selectAvatar(opt.key)}
                                    >
                                        {opt.icon}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {this.state.user && (
                            <div className="settings-section">
                                <div className="settings-label">{strings.nickname}</div>
                                <input
                                    type="text"
                                    className="nickname-input"
                                    placeholder={strings.nicknamePlaceholder}
                                    value={this.state.nickname}
                                    onChange={this.handleNicknameChange}
                                    maxLength={20}
                                />
                            </div>
                        )}
                        <div className="settings-section">
                            <LanguageSwitch language={this.state.language} languages={languages}
                                onLanguageChange={(lang) => this.changeLanguage(lang)} />
                        </div>
                        {!this.state.user && (
                            <div className="settings-section sync-hint">
                                <span className="sync-hint-text">{strings.syncHint}</span>
                                <button className="sync-sign-in-btn" onClick={this.handleSignIn} disabled={this.state.syncing}>
                                    <FontAwesomeIcon icon={faSignInAlt} /> {strings.signIn}
                                </button>
                            </div>
                        )}
                        {this.state.user && (
                            <div className="settings-section sync-status">
                                <span className="sync-badge">&#x2601; {strings.synced}</span>
                                <button className="sync-sign-out-btn" onClick={this.handleSignOut}>
                                    <FontAwesomeIcon icon={faSignOutAlt} /> {strings.signOut}
                                </button>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Quick-start + stats row */}
            <div className="quick-bar">
                {lastWorkout && (
                    <a
                        href={`exercise${lastWorkout.key.charAt(0).toUpperCase() + lastWorkout.key.slice(1)}`}
                        className="quick-start"
                        onClick={this.handleExerciseClick}
                    >
                        <span className="quick-icon">▶</span>
                        <span>{lastWorkout.name}</span>
                    </a>
                )}
                <a href="/dashboard" className="quick-stats">
                    <span>🔥 {stats.currentStreak}</span>
                    <span className="card-dot"></span>
                    <span>💪 {stats.totalSessions}</span>
                </a>
            </div>

            {/* IRIS mascot greeting */}
            <div className="iris-greeting">
                <IrisMascot
                    size="small"
                    mood={getIrisMood(stats)}
                    message={getIrisGreeting(stats)}
                />
            </div>

            {/* ─── Games Section ─────────────────────────────────── */}
            <div className="section-block" data-testid="games-section">
                <div className="section-header">
                    <div className="section-label">
                        <span className="section-icon">🎮</span>
                        <h2 className="section-title">{strings.sectionGames}</h2>
                    </div>
                    <span className="section-desc">{strings.sectionGamesDesc}</span>
                </div>
                <div className="card-grid">
                    <a href="focusCatcher" className="exercise-card game-card" style={{ '--card-accent': '#00E5FF' }}>
                        <div className="card-emoji">🎯</div>
                        <div className="card-name">{strings.focusCatcher}</div>
                        <div className="card-info">
                            <span>{strings.focusCatcherDesc}</span>
                        </div>
                    </a>
                    <a href="rockClimber" className="exercise-card game-card" style={{ '--card-accent': '#E040FB' }}>
                        <div className="card-emoji">🧗</div>
                        <div className="card-name">{strings.rockClimber || 'Rock Climber'}</div>
                        <div className="card-info">
                            <span style={{fontSize:'10px',background:'rgba(255,255,255,0.15)',padding:'2px 6px',borderRadius:'4px',marginRight:'4px'}}>BETA</span>
                            <span>{strings.rockClimberDesc || 'Eye tracking climb'}</span>
                        </div>
                    </a>
                </div>
            </div>

            {/* ─── Eye Training Section ───────────────────────────── */}
            <div className="section-block" data-testid="training-section">
                <div className="section-header">
                    <div className="section-label">
                        <span className="section-icon">👁️</span>
                        <h2 className="section-title">{strings.sectionTraining}</h2>
                    </div>
                    <span className="section-desc">{strings.sectionTrainingDesc}</span>
                </div>
                <div className="card-grid">
                    {allLevels.map(level => this.renderExerciseCard(level))}

                    {/* Self-test as a card */}
                    <a href="selfTest" className="exercise-card self-test-card" style={{ '--card-accent': '#78909C' }}>
                        <div className="card-emoji">👀</div>
                        <div className="card-name">{strings.selfTestTitle}</div>
                        <div className="card-info">
                            <span>{strings.selfTestTitle}</span>
                        </div>
                    </a>
                </div>
            </div>

            {/* Compact medical disclaimer */}
            <div className="disclaimer-bar">
                <span>{strings.disclaimerCompact || 'Not medical advice. Consult your eye doctor before starting exercises.'}</span>
            </div>

            {/* Minimal footer */}
            <footer className="footer">
                <div className="footer-links">
                    <a href="/dashboard">{strings.dashboard}</a>
                    <a href="/policy">{strings.privacyPolicy}</a>
                    <a href="/support">{strings.support}</a>
                </div>
                <p className="footer-text">{strings.builtInQuebec}</p>
            </footer>

        </div>)
    }
}

export default App;
